import React from "react";
import { Search, Filter, AlertCircle } from "lucide-react";
import { UserItem } from "./UserItem";
import { User } from "./UserDetails";

interface FilterState {
  search: string;
  kycStatus: string;
  country: string;
  riskLevel: string;
  userStatus: string;
}

interface UsersListProps {
  users: User[];
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  selectedUsers: string[];
  onSelectUser: (userId: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  onUserAction: (action: string, user: User) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  startIndex: number;
  endIndex: number;
  totalUsers: number;
  loading?: boolean;
}

// Fonction utilitaire pour valider les données
const isValidUser = (user: any): user is User => {
  return !!(user && user.id && user.name && user.email);
};

// Fonction pour obtenir les pays uniques avec sécurité
const getUniqueCountries = (users: User[]): string[] => {
  if (!Array.isArray(users)) return [];

  return [
    ...new Set(
      users
        .filter((user) => user?.country && typeof user.country === "string")
        .map((user) => user.country.trim())
        .filter((country) => country.length > 0)
    ),
  ].sort();
};

// Fonction pour paginer les utilisateurs en toute sécurité
const getCurrentPageUsers = (users: User[], currentPage: number, usersPerPage: number = 10): User[] => {
  if (!Array.isArray(users)) return [];

  const startIdx = (currentPage - 1) * usersPerPage;
  const endIdx = startIdx + usersPerPage;

  return users.slice(startIdx, endIdx).filter((user) => isValidUser(user));
};

// UsersList Component
export const UsersList: React.FC<UsersListProps> = ({
  users,
  filters,
  onFiltersChange,
  selectedUsers,
  onSelectUser,
  onSelectAll,
  onUserAction,
  currentPage,
  totalPages,
  onPageChange,
  startIndex,
  endIndex,
  totalUsers,
  loading,
}) => {
  // Validation des props
  const safeUsers = Array.isArray(users) ? users : [];
  const safeCurrentPage = Math.max(1, Math.min(currentPage, totalPages || 1));
  const safeTotalPages = Math.max(1, totalPages || 1);
  const safeTotalUsers = Math.max(0, totalUsers || 0);

  // Get unique countries for filter
  const countries = getUniqueCountries(safeUsers);

  // Current page users
  const currentUsers = getCurrentPageUsers(safeUsers, safeCurrentPage);
  const allCurrentUsersSelected =
    currentUsers.length > 0 && currentUsers.every((user) => selectedUsers.includes(user.id));

  const handleFilterChange = (key: keyof FilterState, value: string) => {
    if (onFiltersChange) {
      onFiltersChange({
        ...filters,
        [key]: value,
      });
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (onSelectAll) {
      onSelectAll(checked);
    }
  };

  const handlePageChange = (page: number) => {
    if (onPageChange) {
      const safePage = Math.max(1, Math.min(page, safeTotalPages));
      onPageChange(safePage);
    }
  };

  // Si pas d'utilisateurs
  if (safeUsers.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <Filter className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun utilisateur trouvé</h3>
        <p className="text-gray-500">Aucun utilisateur ne correspond aux critères de recherche ou la liste est vide.</p>
      </div>
    );
  }

  return (
    <>
      {/* Filters and Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par nom ou email..."
                value={filters?.search || ""}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
              />
            </div>
          </div>

          <select
            value={filters?.kycStatus || ""}
            onChange={(e) => handleFilterChange("kycStatus", e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white transition-colors"
          >
            <option value="">Tous les statuts KYC</option>
            <option value="pending">En attente</option>
            <option value="approved">Approuvé</option>
            <option value="rejected">Rejeté</option>
            <option value="incomplete">Incomplet</option>
            <option value="not_requested">Non demandé</option>
          </select>

          <select
            value={filters?.country || ""}
            onChange={(e) => handleFilterChange("country", e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white transition-colors"
          >
            <option value="">Tous les pays</option>
            {countries.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>

          <select
            value={filters?.riskLevel || ""}
            onChange={(e) => handleFilterChange("riskLevel", e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white transition-colors"
          >
            <option value="">Tous les niveaux</option>
            <option value="low">Faible risque</option>
            <option value="medium">Risque moyen</option>
            <option value="high">Risque élevé</option>
          </select>
        </div>

        {/* Reset filters */}
        {(filters?.search || filters?.kycStatus || filters?.country || filters?.riskLevel) && (
          <div className="mt-4 flex justify-between items-center">
            <span className="text-sm text-gray-600">{safeTotalUsers} utilisateur(s) correspondant aux filtres</span>
            <button
              onClick={() =>
                onFiltersChange({
                  search: "",
                  kycStatus: "",
                  country: "",
                  riskLevel: "",
                  userStatus: "all",
                })
              }
              className="text-sm text-green-600 hover:text-green-700 font-medium transition-colors"
            >
              Réinitialiser les filtres
            </button>
          </div>
        )}
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto relative">
          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-70 z-10 flex items-center justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
            </div>
          )}
          <table className="w-full min-w-[800px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left w-12">
                  <input
                    type="checkbox"
                    checked={allCurrentUsersSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    disabled={currentUsers.length === 0}
                    className="rounded border-gray-300 text-green-600 focus:ring-green-500 focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600 uppercase tracking-wider">
                  Utilisateur
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600 uppercase tracking-wider">Pays</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600 uppercase tracking-wider">
                  Statut KYC
                </th>

                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600 uppercase tracking-wider">
                  Transactions
                </th>

                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {currentUsers.length > 0 ? (
                currentUsers.map((user) => (
                  <UserItem
                    key={user.id}
                    user={user}
                    isSelected={selectedUsers.includes(user.id)}
                    onSelect={onSelectUser}
                    onAction={onUserAction}
                  />
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center">
                    <AlertCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">Aucun utilisateur à afficher pour cette page</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-600">
              Affichage {Math.min(startIndex + 1, safeTotalUsers)}-{Math.min(endIndex, safeTotalUsers)} sur{" "}
              {safeTotalUsers} utilisateur(s)
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(safeCurrentPage - 1)}
                disabled={safeCurrentPage === 1}
                className="px-3 py-2 border border-gray-300 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors min-w-[80px]"
              >
                Précédent
              </button>

              <div className="flex items-center space-x-1">
                <span className="text-sm text-gray-700 px-2">
                  Page <strong>{safeCurrentPage}</strong> sur <strong>{safeTotalPages}</strong>
                </span>
              </div>

              <button
                onClick={() => handlePageChange(safeCurrentPage + 1)}
                disabled={safeCurrentPage === safeTotalPages}
                className="px-3 py-2 border border-gray-300 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors min-w-[80px]"
              >
                Suivant
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
